import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, TreeSelect, Button, Space, message } from "antd";
import {
    groupCustomerAPIWithErrorHandling,
    GroupCustomerHelpers,
} from "../../../api/groupCustomer/groupCustomer";

const { TextArea } = Input;

/**
 * Drawer thêm mới / chỉnh sửa nhóm khách hàng.
 *
 * Nhóm khách hàng chỉ có 3 trường nên không cần một trang riêng —
 * thao tác ngay trên danh sách giúp giữ được ngữ cảnh và bộ lọc đang bật.
 *
 * @param {boolean} open
 * @param {object|null} record   Có -> chế độ sửa, null -> chế độ thêm mới.
 * @param {object[]} treeData    Cây đơn vị (trang danh sách đã nạp, truyền xuống để không gọi lại API).
 * @param {function} onClose
 * @param {function} onSaved     Gọi sau khi lưu thành công để trang danh sách nạp lại.
 */
export default function GroupCustomerFormDrawer({
    open,
    record = null,
    treeData = [],
    onClose,
    onSaved,
}) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const isEdit = !!record;

    useEffect(() => {
        if (!open) return;

        if (isEdit) {
            form.setFieldsValue({
                name: record.name,
                description: record.description,
                organization_id: record.organization_id
                    ? String(record.organization_id)
                    : record.organization?.id
                        ? String(record.organization.id)
                        : undefined,
            });
        } else {
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, record]);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const groupData = GroupCustomerHelpers.formatGroupCustomerData(values);

            const validation = GroupCustomerHelpers.validateGroupCustomerData(groupData);
            if (!validation.isValid) {
                Object.keys(validation.errors).forEach((field) => {
                    form.setFields([{ name: field, errors: [validation.errors[field]] }]);
                });
                return;
            }

            if (isEdit) {
                await groupCustomerAPIWithErrorHandling.updateGroupCustomer(record.id, groupData);
                message.success("Cập nhật nhóm khách hàng thành công!");
            } else {
                await groupCustomerAPIWithErrorHandling.createGroupCustomer(groupData);
                message.success("Thêm nhóm khách hàng thành công!");
            }

            onSaved?.();
            onClose?.();
        } catch (error) {
            message.error(
                error.message ||
                (isEdit ? "Không thể cập nhật nhóm khách hàng" : "Không thể thêm nhóm khách hàng")
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            title={isEdit ? "Chỉnh sửa nhóm khách hàng" : "Thêm nhóm khách hàng"}
            width="min(460px, 100vw)"
            open={open}
            onClose={onClose}
            destroyOnClose
            maskClosable={!submitting}
            footer={
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={onClose} disabled={submitting}>
                        Hủy
                    </Button>
                    <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                        {isEdit ? "Lưu thay đổi" : "Thêm mới"}
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark>
                <Form.Item
                    name="name"
                    label="Tên nhóm khách hàng"
                    rules={[
                        { required: true, message: "Vui lòng nhập tên nhóm khách hàng!" },
                        { min: 2, message: "Tên nhóm khách hàng phải có ít nhất 2 ký tự!" },
                        { max: 100, message: "Tên nhóm khách hàng không được vượt quá 100 ký tự!" },
                    ]}
                >
                    <Input placeholder="Nhập tên nhóm khách hàng" />
                </Form.Item>

                <Form.Item
                    name="organization_id"
                    label="Thuộc đơn vị"
                    rules={
                        isEdit ? [] : [{ required: true, message: "Vui lòng chọn đơn vị trực thuộc!" }]
                    }
                    // Backend bỏ qua organization_id khi cập nhật (rule đang tắt trong
                    // UpdateGroupCustomerRequest), nên khi sửa chỉ hiển thị để đối chiếu.
                    extra={isEdit ? "Không thể đổi đơn vị sau khi đã tạo nhóm." : undefined}
                >
                    <TreeSelect
                        treeDataSimpleMode
                        style={{ width: "100%" }}
                        styles={{ popup: { root: { maxHeight: 400, overflow: "auto" } } }}
                        placeholder="Chọn đơn vị"
                        treeData={treeData}
                        disabled={isEdit}
                        allowClear
                        showSearch
                        treeNodeFilterProp="title"
                    />
                </Form.Item>

                <Form.Item name="description" label="Chi tiết">
                    <TextArea
                        placeholder="Chi tiết thông tin..."
                        rows={4}
                        maxLength={500}
                        showCount
                    />
                </Form.Item>
            </Form>
        </Drawer>
    );
}
